import classNames from "classnames";
import LoadingDots from "@components/_Layout/Loaders/LoadingDots";
import PropTypes from "prop-types";
import { ColorVariantType } from "../../../../types";
import { useConfig } from "@hooks";

interface LinkButtonProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    ColorVariantType {
  shape?: "default" | "pills" | "outline" | "outline-pills" | "link";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  loadingChildren?: JSX.Element;
  additionalClassname?: string;
}

const Button: React.FC<LinkButtonProps> = ({
  shape = "default",
  variant = "primary",
  size = "md",
  isLoading,
  loadingChildren,
  additionalClassname,
  children,
  ...props
}) => {
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  const LINKBUTTON_CLASS = {
    base: `tw-cursor-pointer tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out`,
    variant: {
      default: {
        shape: {
          default: `tw-py-1 tw-px-3 tw-border tw-border-gray-200 tw-text-gray-800 tw-bg-gray-200 hover:tw-bg-gray-300 tw-rounded`,
          pills: `tw-py-1 tw-px-3 tw-border tw-border-gray-300 tw-text-gray-800 tw-bg-gray-200 hover:tw-bg-gray-300 tw-rounded-full`,
          outline: `tw-py-1 tw-px-3 tw-border tw-border-gray-300 tw-bg-white tw-text-gray-800 hover:tw-bg-gray-300 hover:tw-border-white hover:tw-text-gray-800 tw-rounded`,
          "outline-pills": `tw-py-1 tw-px-3 tw-border tw-border-gray-300 tw-bg-white tw-text-gray-800 hover:tw-bg-gray-300 hover:tw-border-white hover:tw-text-gray-800 tw-rounded-full`,
          link: `tw-text-gray-500 hover:tw-text-gray-600`,
        },
      },
      primary: {
        shape: {
          default: `tw-py-1 tw-px-3 tw-border tw-text-white ${
            mode === "vacanceole"
              ? "tw-border-primary-500 tw-bg-primary-500 hover:tw-bg-primary-400"
              : "tw-border-primary_corseole-500 tw-bg-primary_corseole-500 hover:tw-bg-primary_corseole-400"
          } tw-rounded`,
          pills: `tw-py-1 tw-px-3 tw-border tw-text-white ${
            mode == "vacanceole"
              ? "tw-border-primary-500 tw-bg-primary-500 hover:tw-bg-primary-400"
              : "tw-border-primary_corseole-500 tw-bg-primary_corseole-500 hover:tw-bg-primary_corseole-400"
          } tw-rounded-full`,
          outline: `tw-py-1 tw-px-3 tw-border tw-bg-white ${
            mode === "vacanceole"
              ? "tw-border-primary-500 tw-text-primary-500 hover:tw-bg-primary-500"
              : "tw-border-primary_corseole-500 tw-text-primary_corseole-500 hover:tw-bg-primary_corseole-500"
          } hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-py-1 tw-px-3 tw-border tw-bg-white ${
            mode === "vacanceole"
              ? "tw-border-primary-500 tw-text-primary-500 hover:tw-bg-primary-500"
              : "tw-border-primary_corseole-500 tw-text-primary_corseole-500 hover:tw-bg-primary_corseole-500"
          } hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `${
            mode == "vacanceole"
              ? "tw-text-primary-500 hover:tw-text-primary-400"
              : "tw-text-primary_corseole-500 hover:tw-text-primary_corseole-400"
          }`,
        },
      },
      secondary: {
        shape: {
          default: `tw-py-1 tw-px-3 tw-border tw-border-secondary-500 tw-text-white tw-bg-secondary-500 hover:tw-bg-secondary-400 tw-rounded`,
          pills: `tw-py-1 tw-px-3 tw-border tw-border-secondary-500 tw-text-white tw-bg-secondary-500 hover:tw-bg-secondary-400 tw-rounded-full`,
          outline: `tw-py-1 tw-px-3 tw-border tw-border-secondary-500 tw-bg-white tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-py-1 tw-px-3 tw-border tw-border-secondary-500 tw-bg-white tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-secondary-500 hover:tw-text-secondary-400`,
        },
      },
      success: {
        shape: {
          default: `tw-py-1 tw-px-3 tw-border tw-border-green-500 tw-text-white tw-bg-green-500 hover:tw-bg-green-400 tw-rounded`,
          pills: `tw-py-1 tw-px-3 tw-border tw-border-green-500 tw-text-white tw-bg-green-500 hover:tw-bg-green-400 tw-rounded-full`,
          outline: `tw-py-1 tw-px-3 tw-border tw-border-green-500 tw-bg-white tw-text-green-500 hover:tw-bg-green-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-py-1 tw-px-3 tw-border tw-border-green-500 tw-bg-white tw-text-green-500 hover:tw-bg-green-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-green-500 hover:tw-text-green-400`,
        },
      },
      info: {
        shape: {
          default: `tw-py-1 tw-px-3 tw-border tw-border-blue-500 tw-text-white tw-bg-blue-500 hover:tw-bg-blue-400 tw-rounded`,
          pills: `tw-py-1 tw-px-3 tw-border tw-border-blue-500 tw-text-white tw-bg-blue-500 hover:tw-bg-blue-400 tw-rounded-full`,
          outline: `tw-py-1 tw-px-3 tw-border tw-border-blue-500 tw-bg-white tw-text-blue-500 hover:tw-bg-blue-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-py-1 tw-px-3 tw-border tw-border-blue-500 tw-bg-white tw-text-blue-500 hover:tw-bg-blue-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-blue-500 hover:tw-text-blue-400`,
        },
      },
      warning: {
        shape: {
          default: `tw-py-1 tw-px-3 tw-border tw-border-yellow-500 tw-text-white tw-bg-yellow-500 hover:tw-bg-yellow-400 tw-rounded`,
          pills: `tw-py-1 tw-px-3 tw-border tw-border-yellow-500 tw-text-white tw-bg-yellow-500 hover:tw-bg-yellow-400 tw-rounded-full`,
          outline: `tw-py-1 tw-px-3 tw-border tw-border-yellow-500 tw-bg-white tw-text-yellow-500 hover:tw-bg-yellow-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-py-1 tw-px-3 tw-border tw-border-yellow-500 tw-bg-white tw-text-yellow-500 hover:tw-bg-yellow-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-yellow-500 hover:tw-text-yellow-400`,
        },
      },
      danger: {
        shape: {
          default: `tw-py-1 tw-px-3 tw-border tw-border-red-500 tw-text-white tw-bg-red-500 hover:tw-bg-red-400 tw-rounded`,
          pills: `tw-py-1 tw-px-3 tw-border tw-border-red-500 tw-text-white tw-bg-red-500 hover:tw-bg-red-400 tw-rounded-full`,
          outline: `tw-py-1 tw-px-3 tw-border tw-border-red-500 tw-bg-white tw-text-red-500 hover:tw-bg-red-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-py-1 tw-px-3 tw-border tw-border-red-500 tw-bg-white tw-text-red-500 hover:tw-bg-red-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-red-500 hover:tw-text-red-400`,
        },
      },
    },
    size: {
      sm: `tw-text-sm tw-leading-6`,
      md: `tw-text-base tw-leading-8`,
      lg: `tw-text-lg tw-leading-10`,
    },
  };

  let _buttonClass = classNames(
    LINKBUTTON_CLASS.base,
    LINKBUTTON_CLASS.variant[variant].shape[shape],
    LINKBUTTON_CLASS.size[size],
    additionalClassname
  );
  if (props.className) _buttonClass = props.className;

  return (
    <a className={_buttonClass} {...props}>
      {isLoading ? (
        loadingChildren ? (
          loadingChildren
        ) : (
          <span>
            <LoadingDots />
          </span>
        )
      ) : (
        children
      )}
    </a>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf([
    "default",
    "primary",
    "success",
    "info",
    "warning",
    "danger",
  ]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  isLoading: PropTypes.bool,
  loadingChildren: PropTypes.element,
  additionalClassname: PropTypes.string,
};

export default Button;
